import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { useAIProcessing } from '../context/AIProcessingContext';
import { ImageCropper } from '../components/ImageCropper';
import { useFormExitPrompt, FormExitDialog } from '../hooks/useFormExitPrompt';
import { CurrencyInput } from '../components/CurrencyInput';
import { LocationAutocomplete } from '../components/LocationAutocomplete';
import { useCursorToast } from '../components/CursorToast';
import { 
  Package, 
  DollarSign, 
  MapPin, 
  Tag, 
  FileText,
  Loader2,
  Camera,
  Eye,
  EyeOff,
  ArrowLeft,
  Sparkles,
  Upload,
  Image as ImageIcon,
  Wand2,
  Plus,
  X,
  Check,
  Edit3,
  Trash2,
  ChevronRight,
  ArrowRight,
  RotateCcw,
  CheckCircle,
  AlertTriangle,
  Crop,
  ZoomIn,
  ZoomOut,
  Info,
  Maximize2,
  Crown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from '../lib/toast';
import { MultiPhotoUploader } from '../components/MultiPhotoUploader';
import { Laptop, Sofa, Shirt, Car, Dumbbell, Home, Palette, BookOpen, Wrench, Baby, Music, PawPrint } from 'lucide-react';

const CATEGORY_ICONS = {
  electronics: Laptop,
  furniture: Sofa,
  clothing: Shirt,
  vehicles: Car,
  sports: Dumbbell,
  home: Home,
  collectibles: Palette,
  books: BookOpen,
  tools: Wrench,
  baby: Baby,
  music: Music,
  pets: PawPrint,
  other: Package,
};

const CONDITIONS = [
  { id: 'like_new', name: 'Like New', color: 'text-[#059669]', bg: 'bg-[#059669]/10' },
  { id: 'excellent', name: 'Excellent', color: 'text-[#059669]', bg: 'bg-[#059669]/10' },
  { id: 'good', name: 'Good', color: 'text-[#C9956C]', bg: 'bg-[#C9956C]/10' },
  { id: 'fair', name: 'Fair', color: 'text-[#D97706]', bg: 'bg-[#D97706]/10' },
  { id: 'poor/needs repair', name: 'Poor/Needs Repair', color: 'text-[#DC2626]', bg: 'bg-[#DC2626]/10' },
];

// Simulated AI suggestions based on uploaded photos
const SIMULATED_AI_SUGGESTIONS = [
  {
    id: 'ai-1',
    title: 'Green Leather 2-Seater Couch',
    description: 'Mid-century modern style leather sofa in forest green. Appears to be in excellent condition with minimal wear.',
    category: 'furniture',
    condition: 'excellent',
    estimated_value: 850,
    confidence: 94,
  },
  {
    id: 'ai-2',
    title: 'Brass Table Lamp - Vintage',
    description: 'Antique brass desk lamp with adjustable arm. Art deco style, likely from 1950s-60s.',
    category: 'home',
    condition: 'good',
    estimated_value: 120,
    confidence: 87,
  },
  {
    id: 'ai-3',
    title: 'Persian Rug - Hand-knotted',
    description: 'Traditional Persian rug with intricate floral patterns. Rich reds and blues. Approximately 6x9 feet.',
    category: 'home',
    condition: 'excellent',
    estimated_value: 1200,
    confidence: 82,
  },
  {
    id: 'ai-4',
    title: 'Mahogany Bookshelf - 4 Tier',
    description: 'Solid mahogany wood bookshelf with 4 shelves. Classic design with detailed molding.',
    category: 'furniture',
    condition: 'good',
    estimated_value: 450,
    confidence: 91,
  },
  {
    id: 'ai-5',
    title: 'Atlas Shrugged - First Edition',
    description: 'Ayn Rand\'s Atlas Shrugged, appears to be first edition (1957). Hardcover with dust jacket.',
    category: 'books',
    condition: 'good',
    estimated_value: 2500,
    confidence: 76,
  },
  {
    id: 'ai-6',
    title: 'A Tale of Two Cities - Paperback',
    description: 'Charles Dickens classic. Vintage paperback edition, some yellowing on pages.',
    category: 'books',
    condition: 'fair',
    estimated_value: 15,
    confidence: 95,
  },
];

export default function AddInventoryPage({ mode: initialMode }) {
  const { api, user } = useAuth();
  const { location: browserLocation } = useLocation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { isProcessing: globalProcessing, progress, results: globalResults, tip: globalTip, processPhotos, clearResults } = useAIProcessing();
  
  // Form exit prompt hook for unsaved changes warning
  const { setHasUnsavedChanges, blocker } = useFormExitPrompt();
  
  // Check if user has WANTD+ access (premium or active trial)
  const hasPremiumAccess = user?.is_premium || user?.is_trial_active;
  
  // Use user's profile location if available, otherwise fall back to browser/default location
  const defaultLocation = user?.location && user?.location_name 
    ? { lat: user.location.lat, lng: user.location.lng, name: user.location_name }
    : browserLocation;
  
  const [itemLocation, setItemLocation] = useState(defaultLocation);
  const [useCustomLocation, setUseCustomLocation] = useState(false);
  const [customLocation, setCustomLocation] = useState(null);
  
  // Update location when user profile loads
  useEffect(() => {
    if (user?.location && user?.location_name) {
      const profileLoc = {
        lat: user.location.lat,
        lng: user.location.lng,
        name: user.location_name
      };
      setItemLocation(profileLoc);
    }
  }, [user?.location, user?.location_name]);

  // Get the effective location (custom if toggle is on and set, otherwise profile)
  const effectiveLocation = useCustomLocation && customLocation 
    ? customLocation 
    : itemLocation;
  
  const [mode, setMode] = useState(() => {
    // If single mode is passed, start in manual mode
    if (initialMode === 'single') return 'manual';
    // If photos mode is passed, start directly in AI mode
    if (initialMode === 'photos') return 'ai';
    // Otherwise show selection (null)
    return null;
  });
  
  // Premium access check: If user navigates directly to /list/photos (photos mode), 
  // and they don't have premium, redirect them back to /list
  useEffect(() => {
    if (initialMode === 'photos' && !hasPremiumAccess && user !== null) {
      // User navigated directly to /list/photos without premium access
      navigate('/list', { replace: true });
    }
  }, [initialMode, hasPremiumAccess, user, navigate]);
  const [loading, setLoading] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState({});
  const [editingItem, setEditingItem] = useState(null);
  const [editAllMode, setEditAllMode] = useState(false);
  const [editAllItems, setEditAllItems] = useState({}); // Store edits for all items in edit all mode
  const [aiTip, setAiTip] = useState(null);
  const [croppingItem, setCroppingItem] = useState(null); // For manual re-crop
  const [hoverPreview, setHoverPreview] = useState(null); // For hover image preview { imageUrl, position }
  const [aiFillingForm, setAiFillingForm] = useState(false); // AI assist loading state for single item form
  const [categories, setCategories] = useState([]); // Categories fetched from API
  const [selectedCategory, setSelectedCategory] = useState(null); // Currently selected category object
  const cursorToast = useCursorToast();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    subcategory: '',
    estimated_value: '',
    condition: '',
    is_public: true, // Default to "For Sale"
    is_firm: false,
    is_free: false,
    images: [],
    primary_image_index: 0,
    tags: '',
  });

  // Check if form has any data (is "dirty")
  const hasFormData = formData.title || formData.description || formData.category || 
    formData.estimated_value || formData.condition || formData.images.length > 0 ||
    uploadedPhotos.length > 0 || aiSuggestions.length > 0;

  // Update unsaved changes state when form data changes
  useEffect(() => {
    setHasUnsavedChanges(hasFormData);
  }, [hasFormData, setHasUnsavedChanges]);

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/categories');
        setCategories(response.data);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, [api]);

  // Update selectedCategory when category changes
  useEffect(() => {
    if (formData.category && categories.length > 0) {
      const cat = categories.find(c => c.id === formData.category);
      setSelectedCategory(cat || null);
      // Reset subcategory when category changes
      if (!cat?.subcategories?.some(s => s.id === formData.subcategory)) {
        setFormData(prev => ({ ...prev, subcategory: '' }));
      }
    } else {
      setSelectedCategory(null);
    }
  }, [formData.category, categories]);

  // Handle saving a manual crop
  const handleSaveCrop = async (blob) => {
    if (!croppingItem) return;
    
    try {
      const formData = new FormData();
      formData.append('file', blob, 'crop.jpg');
      
      const response = await api.post('/upload/save-crop', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const newImageUrl = `${backendUrl}${response.data.url}`;
      
      // Update the suggestion with new cropped image
      setAiSuggestions(prev => 
        prev.map(s => s.id === croppingItem.id 
          ? { ...s, preview_image: newImageUrl, images: [newImageUrl] }
          : s
        )
      );
      
      // Also update editingItem if it's the same item
      if (editingItem?.id === croppingItem.id) {
        setEditingItem(prev => ({ ...prev, preview_image: newImageUrl, images: [newImageUrl] }));
      }
      
      toast.success('Image cropped successfully!');
      setCroppingItem(null);
    } catch (error) {
      console.error('Failed to save crop:', error);
      toast.error('Failed to save cropped image');
    }
  };

  // Re-detect a single item using its cropped preview image
  const [redetectingItemId, setRedetectingItemId] = useState(null);
  
  const handleRedetectItem = async (suggestion) => {
    if (redetectingItemId) return; // Already re-detecting
    
    setRedetectingItemId(suggestion.id);
    toast.info('Re-analysing item...');
    
    try {
      // Fetch the current preview image
      const imageUrl = suggestion.preview_image || suggestion.source_photo;
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      
      const formDataUpload = new FormData();
      formDataUpload.append('file', blob, 'redetect.jpg');
      
      const aiResponse = await api.post('/upload/analyze-image', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (aiResponse.data.items && aiResponse.data.items.length > 0) {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const newItem = aiResponse.data.items[0]; // Take the first detected item
        
        // Update the suggestion with new AI data while preserving the ID
        setAiSuggestions(prev => 
          prev.map(s => s.id === suggestion.id 
            ? { 
                ...s, 
                title: newItem.title || s.title,
                description: newItem.description || s.description,
                category: newItem.category || s.category,
                condition: newItem.condition || s.condition,
                condition_rationale: newItem.condition_rationale || s.condition_rationale,
                estimated_value: newItem.estimated_value || s.estimated_value,
                estimated_rrp_new: newItem.estimated_rrp_new || s.estimated_rrp_new,
                confidence: newItem.confidence === 'high' ? 95 : newItem.confidence === 'medium' ? 80 : 60,
                position_hint: newItem.position_hint || s.position_hint,
                preview_image: newItem.preview_image 
                  ? `${backendUrl}${newItem.preview_image}` 
                  : s.preview_image,
                images: newItem.preview_image 
                  ? [`${backendUrl}${newItem.preview_image}`] 
                  : s.images
              }
            : s
          )
        );
        
        toast.success('Item re-analysed successfully!');
      } else {
        toast.warning('Could not identify item. Try cropping differently.');
      }
    } catch (error) {
      console.error('Failed to re-detect item:', error);
      toast.error('Failed to re-analyse item');
    } finally {
      setRedetectingItemId(null);
    }
  };

  // Load results from global context when returning to this page
  // Use ref to track if we've already loaded to prevent reload on delete
  const hasLoadedGlobalResults = useRef(false);
  
  useEffect(() => {
    if (globalResults && globalResults.length > 0 && aiSuggestions.length === 0 && !hasLoadedGlobalResults.current) {
      setAiSuggestions(globalResults);
      setSelectedSuggestions(
        globalResults.reduce((acc, s) => ({ ...acc, [s.id]: true }), {})
      );
      if (globalTip) setAiTip(globalTip);
      setMode('ai');
      hasLoadedGlobalResults.current = true;  // Mark as loaded
    }
  }, [globalResults, globalTip]);

  // Show warning when trying to leave during processing
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (globalProcessing) {
        e.preventDefault();
        e.returnValue = 'AI scanning is in progress. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [globalProcessing]);

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.map((file, index) => ({
      id: `photo-${Date.now()}-${index}`,
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
    }));
    setUploadedPhotos(prev => [...prev, ...newPhotos]);
  };

  const removePhoto = (photoId) => {
    setUploadedPhotos(prev => prev.filter(p => p.id !== photoId));
  };

  const startAIProcessing = async () => {
    if (uploadedPhotos.length === 0) {
      toast.error('Please upload at least one photo');
      return;
    }
    
    // Use global processing context
    const results = await processPhotos(uploadedPhotos);
    if (results && results.length > 0) {
      setAiSuggestions(results);
      setSelectedSuggestions(
        results.reduce((acc, s) => ({ ...acc, [s.id]: true }), {})
      );
      if (globalTip) setAiTip(globalTip);
    }
  };

  const toggleSuggestion = (id) => {
    setSelectedSuggestions(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const removeSuggestion = (id) => {
    setAiSuggestions(prev => prev.filter(s => s.id !== id));
    setSelectedSuggestions(prev => {
      const newState = { ...prev };
      delete newState[id];
      return newState;
    });
  };

  const startEditSuggestion = (suggestion) => {
    setEditingItem({ ...suggestion });
  };

  const saveEditSuggestion = () => {
    setAiSuggestions(prev => 
      prev.map(s => s.id === editingItem.id ? editingItem : s)
    );
    setEditingItem(null);
  };

  // Edit All Mode functions
  const startEditAll = () => {
    // Initialize editAllItems with current suggestions data
    const initialEdits = {};
    aiSuggestions.forEach(s => {
      initialEdits[s.id] = { ...s };
    });
    setEditAllItems(initialEdits);
    setEditAllMode(true);
    setEditingItem(null); // Close any individual edit
  };

  const cancelEditAll = () => {
    setEditAllMode(false);
    setEditAllItems({});
  };

  const saveEditAll = () => {
    // Apply all edits to aiSuggestions
    setAiSuggestions(prev => 
      prev.map(s => editAllItems[s.id] || s)
    );
    setEditAllMode(false);
    setEditAllItems({});
    toast.success('All changes saved');
  };

  const updateEditAllItem = (itemId, field, value) => {
    setEditAllItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value
      }
    }));
  };

  const handleSubmitManual = async (e) => {
    e.preventDefault();
    
    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }
    
    if (!formData.condition) {
      toast.error('Please select the item condition');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        estimated_value: parseFloat(formData.estimated_value),
        condition: formData.condition,
        is_public: formData.is_public,
        is_firm: formData.is_free ? false : formData.is_firm,
        is_free: formData.is_free,
        price: formData.is_free ? 0 : undefined,
        images: formData.images,
        primary_image_index: formData.primary_image_index,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(t => t) : [],
        location: { lat: effectiveLocation.lat, lng: effectiveLocation.lng },
        location_name: effectiveLocation.name,
      };

      console.log('Submitting inventory payload:', JSON.stringify(payload, null, 2));
      await api.post('/inventory/', payload);
      toast.success('Item added to your items!');
      navigate('/my-items');
    } catch (error) {
      const errCode = error.response?.data?.detail?.error;
      if (errCode === 'geo_restricted') {
        toast.error('WANTD is currently available in Australia only.');
      } else if (errCode === 'account_suspended') {
        toast.error(error.response?.data?.detail?.message || 'Your account is suspended.');
      } else if (errCode === 'account_banned') {
        toast.error('Your account has been permanently banned.');
      } else {
        console.error('Inventory submission error:', error.response?.data);
        const detail = error.response?.data?.detail;
        const errorMsg = typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ')
            : 'Failed to add item';
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAI = async () => {
    const itemsToAdd = aiSuggestions.filter(s => selectedSuggestions[s.id]);
    
    if (itemsToAdd.length === 0) {
      toast.error('Please select at least one item to add');
      return;
    }

    setLoading(true);

    try {
      // Add all selected items
      for (const item of itemsToAdd) {
        const payload = {
          title: item.title,
          description: item.description,
          category: item.category,
          condition: item.condition,
          estimated_value: item.estimated_value,
          estimated_rrp_new: item.estimated_rrp_new || null,
          is_public: false,
          is_firm: item.is_firm || false,
          tags: [],
          location: { lat: itemLocation.lat, lng: itemLocation.lng },
          location_name: itemLocation.name,
          // Include the cropped preview image
          images: item.images || (item.preview_image ? [item.preview_image] : []),
          // Include pre-generated semantic keywords (skip AI generation)
          semantic_keywords: item.semantic_keywords || [],
        };

        await api.post('/inventory/', payload);
        
        // Yield between items to prevent blocking
        await new Promise(resolve => setTimeout(resolve, 0));
      }

      // Clear the global AI results after successful submission
      clearResults();
      
      toast.success(`${itemsToAdd.length} item${itemsToAdd.length > 1 ? 's' : ''} added!`);
      navigate('/my-items');
    } catch (error) {
      const errCode = error.response?.data?.detail?.error;
      if (errCode === 'geo_restricted') {
        toast.error('WANTD is currently available in Australia only.');
      } else if (errCode === 'account_suspended') {
        toast.error(error.response?.data?.detail?.message || 'Your account is suspended.');
      } else if (errCode === 'account_banned') {
        toast.error('Your account has been permanently banned.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to add items');
      }
    } finally {
      setLoading(false);
    }
  };

  // AI Assist for single item form - analyzes first uploaded photo and fills form
  const handleAIFillForm = async () => {
    // Check premium access
    if (!hasPremiumAccess) {
      cursorToast.error('AI Form Fill is a WANTD+ feature. Upgrade to unlock smart item analysis!');
      return;
    }
    
    // Need at least one image to analyze
    if (formData.images.length === 0) {
      cursorToast.error('Please upload at least one photo first');
      return;
    }

    setAiFillingForm(true);
    
    try {
      // Get the first image URL and fetch it
      const imageUrl = formData.images[0];
      
      // Convert image URL to blob for upload
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      
      // Create form data for the analyze-single-item endpoint
      const formDataUpload = new FormData();
      formDataUpload.append('file', blob, 'item.jpg');
      
      // Call the backend AI analysis endpoint
      const result = await api.post('/upload/analyze-single-item', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const suggestion = result.data;
      
      // Fill the form with AI suggestions
      setFormData(prev => ({
        ...prev,
        title: suggestion.title || prev.title,
        description: suggestion.description || prev.description,
        category: suggestion.category || prev.category,
        condition: suggestion.condition || prev.condition,
        estimated_value: suggestion.estimated_value || prev.estimated_value,
      }));
      
      toast.success('AI filled form with suggestions! Review and adjust as needed.');
    } catch (error) {
      console.error('AI fill error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to analyze image';
      toast.error(errorMsg);
    } finally {
      setAiFillingForm(false);
    }
  };

  // Mode Selection Screen
  if (mode === null) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          {/* Header */}
          <div className="mb-8">
            <button 
              onClick={() => navigate(-1)} 
              className="flex items-center gap-2 text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)] mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-[#1E3A5F]/10 flex items-center justify-center">
                <Package className="w-7 h-7 text-[var(--theme-text-primary)]" />
              </div>
              <div>
                <h1 className="text-3xl font-black text-[var(--theme-text-primary)]">
                  List Items
                </h1>
                <p className="text-[var(--theme-text-secondary)] mt-1">
                  Add items to sell - we'll match you with interested buyers
                </p>
              </div>
            </div>
          </div>

          {/* Mode Selection */}
          <div className="text-center mb-8">
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-2">How would you like to add items?</h2>
            <p className="text-[var(--theme-text-secondary)]">Choose the method that works best for you</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Manual Option */}
            <button
              onClick={() => setMode('manual')}
              className="p-8 rounded-2xl bg-[var(--theme-card)] border-2 border-[var(--theme-border)] hover:border-[#1E3A5F] transition-all text-left group hover:shadow-lg"
            >
              <div className="w-16 h-16 rounded-2xl bg-[#1E3A5F]/10 flex items-center justify-center mb-4 group-hover:bg-[#1E3A5F] transition-colors">
                <FileText className="w-8 h-8 text-[var(--theme-text-primary)] group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-[var(--theme-text-primary)] mb-2">Add a Specific Item</h3>
              <p className="text-[var(--theme-text-secondary)] mb-4">
                Manually enter details for a single item you want to list for sale.
              </p>
              <div className="flex items-center gap-2 text-[var(--theme-text-primary)] font-medium">
                <span>Get started</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </button>

            {/* AI Photo Option */}
            <button
              onClick={() => setMode('ai')}
              className="p-8 rounded-2xl bg-[var(--theme-card)] border-2 border-[var(--theme-border)] hover:border-[#C9956C] transition-all text-left group hover:shadow-lg relative overflow-hidden"
            >
              <div className="absolute top-4 right-4">
                <span className="px-2 py-1 rounded-full bg-[#C9956C]/10 text-[#C9956C] text-xs font-bold">
                  ✨ SMART
                </span>
              </div>
              <div className="w-16 h-16 rounded-2xl bg-[#C9956C]/10 flex items-center justify-center mb-4 group-hover:bg-[#C9956C] transition-colors">
                <Wand2 className="w-8 h-8 text-[#C9956C] group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-[var(--theme-text-primary)] mb-2">Auto-List from Photo(s)</h3>
              <p className="text-[var(--theme-text-secondary)] mb-4">
                Upload photos and our AI will identify items automatically. Great for multiple items!
              </p>
              <div className="flex items-center gap-2 text-[#C9956C] font-medium">
                <span>Try it out</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </button>
          </div>

          {/* Benefits */}
          <div className="mt-12 p-6 rounded-2xl bg-[#1E3A5F]/5 border border-[#1E3A5F]/10">
            <h4 className="font-bold text-[var(--theme-text-primary)] mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#C9956C]" />
              Why List on WANTD?
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#059669] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-[var(--theme-text-primary)]">Zero Fees for Sellers</p>
                  <p className="text-sm text-[var(--theme-text-secondary)]">Buyers pay the marketplace fee, not you</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#059669] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-[var(--theme-text-primary)]">No Commitment</p>
                  <p className="text-sm text-[var(--theme-text-secondary)]">Only sell when a good offer comes in</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#059669] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-[var(--theme-text-primary)]">Smart Matching</p>
                  <p className="text-sm text-[var(--theme-text-secondary)]">We find buyers looking for exactly your items</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // AI Photo Mode
  if (mode === 'ai') {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          {/* Header */}
          <div className="mb-8">
            <button 
              onClick={() => {
                if (aiSuggestions.length > 0) {
                  setAiSuggestions([]);
                  setUploadedPhotos([]);
                  clearResults();  // Clear global AI processing results
                } else if (initialMode === 'photos') {
                  // If came directly from /list/photos, go back to /list
                  navigate('/list');
                } else {
                  setMode(null);
                }
              }} 
              className="flex items-center gap-2 text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)] mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {aiSuggestions.length > 0 ? 'Start Over' : 'Back to options'}
            </button>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-[#C9956C]/10 flex items-center justify-center">
                <Wand2 className="w-7 h-7 text-[#C9956C]" />
              </div>
              <div>
                <h1 className="text-3xl font-black text-[var(--theme-text-primary)]">
                  Auto-List from Photo(s)
                </h1>
                <p className="text-[var(--theme-text-secondary)] mt-1">
                  Upload photos and we'll identify items for you
                </p>
              </div>
            </div>
          </div>

          {aiSuggestions.length === 0 ? (
            /* Upload Phase */
            <div className="space-y-6">
              {/* Upload Area */}
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="p-12 rounded-2xl border-2 border-dashed border-[var(--theme-border)] hover:border-[#C9956C] bg-[var(--theme-card)] cursor-pointer transition-colors text-center"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
                <div className="w-20 h-20 rounded-full bg-[#C9956C]/10 flex items-center justify-center mx-auto mb-4">
                  <Camera className="w-10 h-10 text-[#C9956C]" />
                </div>
                <h3 className="text-xl font-bold text-[var(--theme-text-primary)] mb-2">Drop photos here or click to upload</h3>
                <p className="text-[var(--theme-text-secondary)] mb-4">
                  Supports JPG, PNG, HEIC • Multiple photos welcome
                </p>
                <Button className="bg-[#C9956C] hover:bg-[#B8845B] rounded-full">
                  <Upload className="w-4 h-4 mr-2" />
                  Select Photos
                </Button>
              </div>

              {/* Uploaded Photos Preview */}
              {uploadedPhotos.length > 0 && (
                <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-[var(--theme-text-primary)]">
                      {uploadedPhotos.length} Photo{uploadedPhotos.length > 1 ? 's' : ''} Ready
                    </h3>
                    <button 
                      onClick={() => fileInputRef.current?.click()}
                      className="text-sm text-[#C9956C] hover:text-[#B8845B] font-medium flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      Add More
                    </button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {uploadedPhotos.map((photo) => (
                      <div key={photo.id} className="relative aspect-square rounded-xl overflow-hidden group">
                        <img 
                          src={photo.preview} 
                          alt={photo.name}
                          className="w-full h-full object-cover"
                        />
                        <button
                          onClick={(e) => { e.stopPropagation(); removePhoto(photo.id); }}
                          className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Process Button - Theme-aware styling */}
              {uploadedPhotos.length > 0 && (
                <Button
                  onClick={startAIProcessing}
                  disabled={globalProcessing}
                  className="w-full h-14 bg-[#C9956C] hover:bg-[#B8845B] text-white rounded-full text-lg font-semibold shadow-lg"
                >
                  {globalProcessing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Analysing Photos ({progress.current}/{progress.total})...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-5 h-5 mr-2" />
                      Identify Items with AI
                    </>
                  )}
                </Button>
              )}

              {/* Tips - no duplicate status message, just the tip */}
              <div className={`p-4 rounded-xl bg-[#1E3A5F]/5 border border-[#1E3A5F]/10 ${globalProcessing ? 'mb-32' : ''}`}>
                <p className="text-sm text-[var(--theme-text-secondary)]">
                  <strong className="text-[var(--theme-text-primary)]">Tip:</strong> For best results, take clear photos with good lighting. 
                  Include multiple angles for valuable items. Our AI can identify multiple items from a single room photo!
                </p>
              </div>
            </div>
          ) : (
            /* Results Phase */
            <div className="space-y-6">
              {/* Results Header */}
              <div className="p-4 rounded-xl bg-[#059669]/10 border border-[#059669]/20">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-[#059669]" />
                  <div>
                    <p className="font-bold text-[var(--theme-text-primary)]">
                      Found {aiSuggestions.length} item{aiSuggestions.length > 1 ? 's' : ''} in your photos
                    </p>
                    <p className="text-sm text-[var(--theme-text-secondary)]">
                      Review and edit the suggestions below, then list them
                    </p>
                    <p className="text-xs text-[var(--theme-text-muted)] mt-1 italic">
                      Pricing estimates are generated by AI based on similar items in the secondhand market.
                    </p>
                  </div>
                </div>
              </div>

              {/* AI Tip */}
              {aiTip && (
                <div className="p-4 rounded-xl bg-[#C9956C]/10 border border-[#C9956C]/20">
                  <div className="flex items-start gap-3">
                    <Camera className="w-5 h-5 text-[#C9956C] flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-[var(--theme-text-secondary)]">
                      <strong className="text-[#C9956C]">Pro tip:</strong> {aiTip}
                    </p>
                  </div>
                </div>
              )}

              {/* Edit All Controls */}
              {aiSuggestions.length > 1 && (
                <div className="flex items-center justify-between">
                  {editAllMode ? (
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-[var(--theme-text-primary)]">
                        Editing all {aiSuggestions.length} items
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={cancelEditAll}
                        className="rounded-full"
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={saveEditAll}
                        className="rounded-full bg-[#059669] hover:bg-[#047857]"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Save All
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={startEditAll}
                      className="rounded-full border-[#C9956C] text-[#C9956C] hover:bg-[#C9956C]/10"
                    >
                      <Edit3 className="w-4 h-4 mr-1" />
                      Edit All
                    </Button>
                  )}
                </div>
              )}

              {/* Suggestions List */}
              <div className="space-y-4">
                {aiSuggestions.map((suggestion) => (
                  <div 
                    key={suggestion.id}
                    className={`p-5 rounded-2xl bg-[var(--theme-card)] border-2 transition-all ${
                      selectedSuggestions[suggestion.id] 
                        ? 'border-[#1E3A5F] shadow-md' 
                        : 'border-[var(--theme-border)]'
                    }`}
                  >
                    {editingItem?.id === suggestion.id ? (
                      /* Individual Edit Mode */
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="font-bold text-[var(--theme-text-primary)]">Edit Item</h4>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEditingItem(null)}
                              className="rounded-full"
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={saveEditSuggestion}
                              className="rounded-full bg-[#059669] hover:bg-[#047857]"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Save
                            </Button>
                          </div>
                        </div>
                        
                        {/* Image Preview & Quick Zoom */}
                        <div className="p-3 rounded-xl bg-[var(--theme-hover)]">
                          <p className="text-sm text-[var(--theme-text-secondary)] mb-3">Make adjustments to ensure the item is correct and in full view</p>
                          <div className="flex items-start gap-4">
                            <div 
                              className="relative w-28 h-28 rounded-lg overflow-hidden flex-shrink-0 border border-[var(--theme-border)] bg-[var(--theme-card)] cursor-pointer group"
                              onMouseEnter={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setHoverPreview({
                                  imageUrl: editingItem.preview_image || editingItem.source_photo,
                                  title: editingItem.title,
                                  x: rect.left,
                                  y: rect.top
                                });
                              }}
                              onMouseLeave={() => setHoverPreview(null)}
                              onClick={() => setCroppingItem(editingItem)}
                            >
                              <img 
                                src={editingItem.preview_image || editingItem.source_photo} 
                                alt={editingItem.title}
                                className="w-full h-full object-contain"
                                style={{ transform: `scale(${editingItem.previewZoom || 1})` }}
                              />
                              <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                <Crop className="w-5 h-5 text-white" />
                              </div>
                            </div>
                            <div className="flex-1 space-y-3">
                              {/* Quick Zoom Slider */}
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs text-[var(--theme-text-secondary)]">Quick zoom</span>
                                  <span className="text-xs text-[var(--theme-text-primary)] font-medium">{Math.round((editingItem.previewZoom || 1) * 100)}%</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <ZoomOut className="w-4 h-4 text-[var(--theme-text-secondary)]" />
                                  <input
                                    type="range"
                                    min="50"
                                    max="200"
                                    value={(editingItem.previewZoom || 1) * 100}
                                    onChange={(e) => setEditingItem(prev => ({ 
                                      ...prev, 
                                      previewZoom: parseInt(e.target.value) / 100 
                                    }))}
                                    className="flex-1 h-2 bg-[#E5DDD3] rounded-full appearance-none cursor-pointer
                                      [&::-webkit-slider-thumb]:appearance-none 
                                      [&::-webkit-slider-thumb]:w-4 
                                      [&::-webkit-slider-thumb]:h-4 
                                      [&::-webkit-slider-thumb]:bg-[#1E3A5F] 
                                      [&::-webkit-slider-thumb]:rounded-full
                                      [&::-webkit-slider-thumb]:cursor-pointer"
                                  />
                                  <ZoomIn className="w-4 h-4 text-[var(--theme-text-secondary)]" />
                                </div>
                              </div>
                              
                              {/* Adjust Crop Button */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCroppingItem(editingItem)}
                                className="rounded-full border-[#C9956C] text-[#C9956C] hover:bg-[#C9956C]/10"
                              >
                                <Crop className="w-4 h-4 mr-1" />
                                Advanced Crop
                              </Button>
                            </div>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-[var(--theme-text-primary)] mb-1">Title</label>
                            <Input
                              value={editingItem.title}
                              onChange={(e) => setEditingItem(prev => ({ ...prev, title: e.target.value }))}
                              className="bg-[var(--theme-hover)] border-[var(--theme-border)]"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-[var(--theme-text-primary)] mb-1">Value ($)</label>
                            <Input
                              type="number"
                              value={editingItem.estimated_value}
                              onChange={(e) => setEditingItem(prev => ({ ...prev, estimated_value: parseFloat(e.target.value) }))}
                              className="bg-[var(--theme-hover)] border-[var(--theme-border)]"
                            />
                            <button
                              type="button"
                              onClick={() => setEditingItem(prev => ({ ...prev, is_firm: !prev.is_firm }))}
                              className="mt-2 flex items-center gap-2 cursor-pointer group"
                            >
                              <span className={`w-4 h-4 rounded flex items-center justify-center border-2 transition-all flex-shrink-0 ${
                                editingItem.is_firm ? 'bg-[#1E3A5F] border-[#1E3A5F]' : 'border-[#D4CCC2] group-hover:border-[#1E3A5F]/50'
                              }`}>
                                {editingItem.is_firm && <Check className="w-2.5 h-2.5 text-white" />}
                              </span>
                              <div>
                                <span className="block text-xs font-medium text-[var(--theme-text-primary)]">Firm price</span>
                                <span className="block text-[10px] text-[var(--theme-text-muted)]">(no offers below)</span>
                              </div>
                            </button>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-[var(--theme-text-primary)] mb-1">Category</label>
                            <Select 
                              value={editingItem.category}
                              onValueChange={(value) => setEditingItem(prev => ({ ...prev, category: value }))}
                            >
                              <SelectTrigger className="bg-[var(--theme-hover)] border-[var(--theme-border)]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[var(--theme-card)] border-[var(--theme-border)]">
                                {categories.map((cat) => {
                                  const IconComponent = CATEGORY_ICONS[cat.id] || Package;
                                  return (
                                    <SelectItem key={cat.id} value={cat.id}>
                                      <span className="flex items-center gap-2">
                                        <IconComponent className="w-4 h-4" />
                                        <span>{cat.name}</span>
                                      </span>
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-[var(--theme-text-primary)] mb-1">Condition</label>
                            <Select 
                              value={editingItem.condition}
                              onValueChange={(value) => setEditingItem(prev => ({ ...prev, condition: value }))}
                            >
                              <SelectTrigger className="bg-[var(--theme-hover)] border-[var(--theme-border)]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[var(--theme-card)] border-[var(--theme-border)]">
                                {CONDITIONS.map((cond) => (
                                  <SelectItem key={cond.id} value={cond.id}>
                                    <span className={cond.color}>{cond.name}</span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[var(--theme-text-primary)] mb-1">Description</label>
                          <Textarea
                            value={editingItem.description}
                            onChange={(e) => setEditingItem(prev => ({ ...prev, description: e.target.value }))}
                            className="bg-[var(--theme-hover)] border-[var(--theme-border)] min-h-[80px]"
                          />
                        </div>
                      </div>
                    ) : editAllMode ? (
                      /* Edit All Mode - Compact inline editing */
                      <div className="flex items-start gap-4">
                        <button
                          onClick={() => toggleSuggestion(suggestion.id)}
                          className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-colors ${
                            selectedSuggestions[suggestion.id]
                              ? 'bg-[#1E3A5F] border-[#1E3A5F]'
                              : 'border-[#D4CCC2]'
                          }`}
                        >
                          {selectedSuggestions[suggestion.id] && (
                            <Check className="w-4 h-4 text-white" />
                          )}
                        </button>
                        
                        {/* Preview Image */}
                        <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-[#F5F0E8] dark:bg-gray-800 border border-[var(--theme-border)]">
                          <img 
                            src={suggestion.preview_image || suggestion.source_photo} 
                            alt={editAllItems[suggestion.id]?.title || suggestion.title}
                            className="w-full h-full object-contain"
                          />
                        </div>
                        
                        {/* Editable Fields */}
                        <div className="flex-1 min-w-0 space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-[var(--theme-text-secondary)] mb-1">Title</label>
                              <Input
                                value={editAllItems[suggestion.id]?.title || suggestion.title}
                                onChange={(e) => updateEditAllItem(suggestion.id, 'title', e.target.value)}
                                className="bg-[var(--theme-hover)] border-[var(--theme-border)] h-9 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-[var(--theme-text-secondary)] mb-1">Value ($)</label>
                              <Input
                                type="number"
                                value={editAllItems[suggestion.id]?.estimated_value || suggestion.estimated_value}
                                onChange={(e) => updateEditAllItem(suggestion.id, 'estimated_value', parseFloat(e.target.value))}
                                className="bg-[var(--theme-hover)] border-[var(--theme-border)] h-9 text-sm"
                              />
                              <button
                                type="button"
                                onClick={() => updateEditAllItem(suggestion.id, 'is_firm', !(editAllItems[suggestion.id]?.is_firm ?? suggestion.is_firm ?? false))}
                                className="mt-1.5 flex items-center gap-1.5 cursor-pointer group"
                              >
                                <span className={`w-4 h-4 rounded flex items-center justify-center border-2 transition-all flex-shrink-0 ${
                                  (editAllItems[suggestion.id]?.is_firm ?? suggestion.is_firm ?? false) ? 'bg-[#1E3A5F] border-[#1E3A5F]' : 'border-[#D4CCC2] group-hover:border-[#1E3A5F]/50'
                                }`}>
                                  {(editAllItems[suggestion.id]?.is_firm ?? suggestion.is_firm ?? false) && <Check className="w-2.5 h-2.5 text-white" />}
                                </span>
                                <span className="text-[10px] font-medium text-[var(--theme-text-secondary)]">Firm price (no offers below)</span>
                              </button>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-[var(--theme-text-secondary)] mb-1">Category</label>
                              <Select 
                                value={editAllItems[suggestion.id]?.category || suggestion.category}
                                onValueChange={(value) => updateEditAllItem(suggestion.id, 'category', value)}
                              >
                                <SelectTrigger className="bg-[var(--theme-hover)] border-[var(--theme-border)] h-9 text-sm">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[var(--theme-card)] border-[var(--theme-border)]">
                                  {categories.map((cat) => {
                                    const IconComponent = CATEGORY_ICONS[cat.id] || Package;
                                    return (
                                      <SelectItem key={cat.id} value={cat.id}>
                                        <span className="flex items-center gap-2">
                                          <IconComponent className="w-4 h-4" />
                                          {cat.name}
                                        </span>
                                      </SelectItem>
                                    );
                                  })}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-[var(--theme-text-secondary)] mb-1">Condition</label>
                              <Select 
                                value={editAllItems[suggestion.id]?.condition || suggestion.condition}
                                onValueChange={(value) => updateEditAllItem(suggestion.id, 'condition', value)}
                              >
                                <SelectTrigger className="bg-[var(--theme-hover)] border-[var(--theme-border)] h-9 text-sm">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[var(--theme-card)] border-[var(--theme-border)]">
                                  {CONDITIONS.map((cond) => (
                                    <SelectItem key={cond.id} value={cond.id}>
                                      <span className={cond.color}>{cond.name}</span>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-[var(--theme-text-secondary)] mb-1">Description</label>
                            <Textarea
                              value={editAllItems[suggestion.id]?.description || suggestion.description}
                              onChange={(e) => updateEditAllItem(suggestion.id, 'description', e.target.value)}
                              className="bg-[var(--theme-hover)] border-[var(--theme-border)] min-h-[60px] text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* View Mode */
                      <div className="flex items-start gap-4">
                        <button
                          onClick={() => toggleSuggestion(suggestion.id)}
                          className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-colors ${
                            selectedSuggestions[suggestion.id]
                              ? 'bg-[#1E3A5F] border-[#1E3A5F]'
                              : 'border-[#D4CCC2]'
                          }`}
                        >
                          {selectedSuggestions[suggestion.id] && (
                            <Check className="w-4 h-4 text-white" />
                          )}
                        </button>
                        
                        {/* Preview Image with Re-crop button */}
                        <div 
                          className="relative w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-[#F5F0E8] dark:bg-gray-800 border border-[var(--theme-border)] group cursor-pointer"
                          onMouseEnter={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setHoverPreview({
                              imageUrl: suggestion.preview_image || suggestion.source_photo,
                              title: suggestion.title,
                              x: rect.left,
                              y: rect.top
                            });
                          }}
                          onMouseLeave={() => setHoverPreview(null)}
                          onClick={() => setCroppingItem(suggestion)}
                        >
                          <img 
                            src={suggestion.preview_image || suggestion.source_photo} 
                            alt={suggestion.title}
                            className="w-full h-full object-contain"
                          />
                          {/* Quick re-crop button overlay */}
                          <div
                            className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Crop className="w-5 h-5 text-white" />
                          </div>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4 mb-2">
                            <div>
                              <h4 className="font-bold text-[var(--theme-text-primary)] text-lg">{suggestion.title}</h4>
                              <div className="flex items-center flex-wrap gap-2 mt-1">
                                <span className="text-xs px-2 py-0.5 rounded bg-[#F5F0E8] text-[var(--theme-text-secondary)] capitalize">
                                  {categories.find(c => c.id === suggestion.category)?.name || suggestion.category}
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded ${CONDITIONS.find(c => c.id === suggestion.condition)?.bg} ${CONDITIONS.find(c => c.id === suggestion.condition)?.color} capitalize`}>
                                  {suggestion.condition.replace('_', ' ')}
                                </span>
                                <span className="text-xs text-[#9CA3AF]">
                                  {suggestion.confidence}% confidence
                                </span>
                                {/* position_hint is stored but not shown to user */}
                                {suggestion.verification_warning && (
                                  <span className="text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400 font-medium">
                                    ⚠️ Review needed
                                  </span>
                                )}
                              </div>
                              {/* Verification Warning */}
                              {suggestion.verification_warning && (
                                <div className="flex items-start gap-1.5 mt-2 text-xs text-amber-700 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400 px-2 py-1.5 rounded-lg">
                                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                  <span>The image may not match this description. Use "Re-detect" to re-analyse, or manually verify.</span>
                                </div>
                              )}
                              {/* Condition Rationale */}
                              {suggestion.condition_rationale && !suggestion.verification_warning && (
                                <div className="flex items-start gap-1.5 mt-2 text-xs text-[var(--theme-text-secondary)] bg-[var(--theme-hover)] px-2 py-1.5 rounded-lg">
                                  <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-[#C9956C]" />
                                  <span>{suggestion.condition_rationale}</span>
                                </div>
                              )}
                            </div>
                            {/* Pricing info removed per UAT REF 63 */}
                          </div>
                          <p className="text-sm text-[var(--theme-text-secondary)] mb-3">{suggestion.description}</p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              onClick={() => handleRedetectItem(suggestion)}
                              disabled={redetectingItemId === suggestion.id}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm text-[#C9956C] hover:bg-[#C9956C]/10 transition-colors disabled:opacity-50"
                              title="Re-analyse this item's image"
                            >
                              {redetectingItemId === suggestion.id ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <RotateCcw className="w-3.5 h-3.5" />
                              )}
                              Re-detect
                            </button>
                            <button
                              onClick={() => startEditSuggestion(suggestion)}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm text-[var(--theme-text-primary)] hover:bg-[var(--theme-hover)] transition-colors"
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                              Edit
                            </button>
                            <button
                              onClick={() => removeSuggestion(suggestion.id)}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm text-[#DC2626] hover:bg-red-50 transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              Remove
                            </button>
                            <button
                              onClick={() => setAiSuggestions(prev => prev.map(s => s.id === suggestion.id ? { ...s, is_firm: !s.is_firm } : s))}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors group"
                            >
                              <span className={`w-4 h-4 rounded flex items-center justify-center border-2 transition-all flex-shrink-0 ${
                                suggestion.is_firm ? 'bg-[#1E3A5F] border-[#1E3A5F]' : 'border-[#D4CCC2] group-hover:border-[#1E3A5F]/50'
                              }`}>
                                {suggestion.is_firm && <Check className="w-2.5 h-2.5 text-white" />}
                              </span>
                              <span className={suggestion.is_firm ? 'text-[#1E3A5F] font-medium' : 'text-[var(--theme-text-secondary)]'}>
                                Firm price
                              </span>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Summary & Submit - with bottom padding to avoid overlap with floating indicator */}
              <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card mb-24">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-[var(--theme-text-secondary)]">Selected Items</p>
                    <p className="text-2xl font-black text-[var(--theme-text-primary)]">
                      {Object.values(selectedSuggestions).filter(Boolean).length} of {aiSuggestions.length}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-[var(--theme-text-secondary)]">Total Estimated Value</p>
                    <p className="text-2xl font-black text-[#059669]">
                      ${aiSuggestions
                        .filter(s => selectedSuggestions[s.id])
                        .reduce((sum, s) => sum + s.estimated_value, 0)
                        .toLocaleString()}
                    </p>
                  </div>
                </div>
                {/* Pricing disclaimer moved to Results Header per UAT REF 64 */}
                <Button
                  onClick={handleSubmitAI}
                  disabled={loading || Object.values(selectedSuggestions).filter(Boolean).length === 0}
                  className="w-full h-14 bg-[#1E3A5F] hover:bg-[#2D4A6F] rounded-full text-lg font-semibold"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Plus className="w-5 h-5 mr-2" />
                      Add {Object.values(selectedSuggestions).filter(Boolean).length} Item{Object.values(selectedSuggestions).filter(Boolean).length !== 1 ? 's' : ''} to My Items
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
        
        {/* Hover Preview Popup */}
        {hoverPreview && (
          <div 
            className="fixed z-50 pointer-events-none"
            style={{
              left: hoverPreview.x < window.innerWidth / 2 ? hoverPreview.x + 90 : hoverPreview.x - 330,
              top: Math.max(20, Math.min(hoverPreview.y - 100, window.innerHeight - 350))
            }}
          >
            <div className="bg-white dark:bg-[#1a1a2e] rounded-xl shadow-2xl border border-[var(--theme-border)] p-2 animate-in fade-in zoom-in-95 duration-150">
              <div className="relative w-80 h-80 rounded-lg overflow-hidden bg-[#F5F0E8] dark:bg-gray-800">
                <img 
                  src={hoverPreview.imageUrl} 
                  alt={hoverPreview.title}
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="mt-2 px-1">
                <p className="text-sm font-medium text-[var(--theme-text-primary)] truncate">{hoverPreview.title}</p>
                <p className="text-xs text-[var(--theme-text-secondary)] flex items-center gap-1 mt-1">
                  <Maximize2 className="w-3 h-3" />
                  Click to crop
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Image Cropper Modal */}
        {croppingItem && (
          <ImageCropper
            imageUrl={
              croppingItem.source_photo || 
              (croppingItem.source_image?.startsWith('/') 
                ? `${process.env.REACT_APP_BACKEND_URL}${croppingItem.source_image}` 
                : croppingItem.source_image) ||
              croppingItem.preview_image
            }
            itemTitle={croppingItem.title}
            onSave={handleSaveCrop}
            onCancel={() => setCroppingItem(null)}
            detectionBox={croppingItem.detection_box}
          />
        )}
      </Layout>
    );
  }

  // Manual Mode
  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={() => setMode(null)} 
            className="flex items-center gap-2 text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)] mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to options
          </button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-[#1E3A5F]/10 flex items-center justify-center">
              <FileText className="w-7 h-7 text-[var(--theme-text-primary)]" />
            </div>
            <div>
              <h1 className="text-3xl font-black text-[var(--theme-text-primary)]">
                Add a Specific Item
              </h1>
              <p className="text-[var(--theme-text-secondary)] mt-1">
                Enter the details for the item you want to add
              </p>
            </div>
          </div>
        </div>

        {/* Form + Live Preview Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Form Column */}
        <div className="lg:col-span-3">
        <form onSubmit={handleSubmitManual} className="space-y-6">
          {/* Item Details */}
          <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card space-y-5">
            <h2 className="font-bold text-[var(--theme-text-primary)] text-lg flex items-center gap-2">
              <Package className="w-5 h-5 text-[var(--theme-text-primary)]" />
              Item Details
            </h2>
            
            {/* Photos Section - moved inside Item Details */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-semibold text-[var(--theme-text-primary)]">
                  Photos
                </label>
                {/* AI Fill Button - only show when images are uploaded */}
                {formData.images.length > 0 && (
                  <Button
                    type="button"
                    onClick={handleAIFillForm}
                    disabled={aiFillingForm}
                    className={`rounded-full text-sm h-9 px-4 ${
                      hasPremiumAccess 
                        ? 'bg-gradient-to-r from-[#C9956C] to-[#B8845B] hover:from-[#B8845B] hover:to-[#A7744A] text-white' 
                        : 'bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] border border-[var(--theme-border)]'
                    }`}
                    data-testid="ai-fill-form-btn"
                  >
                    {aiFillingForm ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-4 h-4 mr-2" />
                        AI Fill Form
                        {!hasPremiumAccess && <Crown className="w-3 h-3 ml-1 text-[#C9956C]" />}
                      </>
                    )}
                  </Button>
                )}
              </div>
              <p className="text-sm text-[var(--theme-text-secondary)] flex items-start gap-2 bg-[var(--theme-hover)] p-3 rounded-lg">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#C9956C]" />
                <span>Listings work best with multiple photos of a <strong>single item</strong>. Avoid combining different items in one listing.</span>
              </p>
              <MultiPhotoUploader
                images={formData.images}
                primaryIndex={formData.primary_image_index}
                onImagesChange={(images) => setFormData(prev => ({ ...prev, images }))}
                onPrimaryChange={(index) => setFormData(prev => ({ ...prev, primary_image_index: index }))}
                maxImages={6}
                api={api}
                compact={true}
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-[var(--theme-text-primary)] mb-2">
                What is it? <span className="text-[#DC2626]">*</span>
              </label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Green Leather 2-Seater Couch"
                className="bg-[var(--theme-hover)] border-[var(--theme-border)] text-[var(--theme-text-primary)] h-12"
                required
                data-testid="inventory-title-input"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[var(--theme-text-primary)] mb-2">
                Category <span className="text-[#DC2626]">*</span>
              </label>
              <Select 
                value={formData.category} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger className="bg-[var(--theme-hover)] border-[var(--theme-border)] h-12" data-testid="inventory-category-select">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--theme-card)] border-[var(--theme-border)]">
                  {categories.map((cat) => {
                    const IconComponent = CATEGORY_ICONS[cat.id] || Package;
                    return (
                      <SelectItem key={cat.id} value={cat.id} className="hover:bg-[var(--theme-hover)]">
                        <span className="flex items-center gap-2">
                          <IconComponent className="w-4 h-4" />
                          <span>{cat.name}</span>
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Subcategory - Only show if category has subcategories */}
            {selectedCategory?.subcategories?.length > 0 && (
              <div>
                <label className="block text-sm font-semibold text-[var(--theme-text-primary)] mb-2">
                  Subcategory
                </label>
                <Select 
                  value={formData.subcategory} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, subcategory: value }))}
                >
                  <SelectTrigger className="bg-[var(--theme-hover)] border-[var(--theme-border)] h-12" data-testid="inventory-subcategory-select">
                    <SelectValue placeholder="Select a subcategory (optional)" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--theme-card)] border-[var(--theme-border)]">
                    {selectedCategory.subcategories.map((sub) => (
                      <SelectItem key={sub.id} value={sub.id} className="hover:bg-[var(--theme-hover)]">
                        {sub.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-[var(--theme-text-primary)] mb-2">
                Description <span className="text-[#DC2626]">*</span>
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the item - brand, model, size, color, any defects or notable features..."
                className="bg-[var(--theme-hover)] border-[var(--theme-border)] text-[var(--theme-text-primary)] min-h-[100px]"
                required
                data-testid="inventory-description-input"
              />
            </div>
          </div>

          {/* Value & Condition */}
          <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card space-y-5">
            <h2 className="font-bold text-[var(--theme-text-primary)] text-lg flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#059669]" />
              Value & Condition
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-semibold text-[var(--theme-text-primary)] mb-2">
                  Estimated Value <span className="text-[#DC2626]">*</span>
                </label>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <CurrencyInput
                      value={formData.is_free ? '0' : formData.estimated_value}
                      onChange={(value) => !formData.is_free && setFormData(prev => ({ ...prev, estimated_value: value }))}
                      placeholder="What would you sell it for?"
                      className={`h-12 ${formData.is_free ? 'opacity-50 cursor-not-allowed' : ''}`}
                      required
                      disabled={formData.is_free}
                      dataTestId="inventory-value-input"
                    />
                  </div>
                  {/* FREE toggle */}
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      is_free: !prev.is_free,
                      is_firm: prev.is_free ? prev.is_firm : false,
                    }))}
                    className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg border-2 font-bold text-sm transition-all ${
                      formData.is_free
                        ? 'bg-[#059669] border-[#059669] text-white'
                        : 'bg-transparent border-[#D4CCC2] text-[var(--theme-text-muted)] hover:border-[#059669] hover:text-[#059669]'
                    }`}
                    data-testid="free-toggle"
                  >
                    <Check className={`w-3.5 h-3.5 ${formData.is_free ? 'opacity-100' : 'opacity-0'}`} />
                    FREE
                  </button>
                  {!formData.is_free && (
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, is_firm: !prev.is_firm }))}
                      className="flex-shrink-0 flex items-center gap-2 cursor-pointer group"
                      data-testid="firm-price-toggle"
                    >
                      <span className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-all ${
                        formData.is_firm ? 'bg-[#1E3A5F] border-[#1E3A5F]' : 'border-[#D4CCC2] group-hover:border-[#1E3A5F]/50'
                      }`}>
                        {formData.is_firm && <Check className="w-3 h-3 text-white" />}
                      </span>
                      <div>
                        <span className="block text-sm font-medium text-[var(--theme-text-primary)]">Firm price</span>
                        <span className="block text-xs text-[var(--theme-text-muted)]">(no offers below)</span>
                      </div>
                    </button>
                  )}
                </div>
                {formData.is_free && (
                  <p className="mt-2 text-xs text-[#059669] flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5" />
                    Listed for free — expires in 14 days, no marketplace fee
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-[var(--theme-text-primary)] mb-2">
                  Condition <span className="text-[#DC2626]">*</span>
                </label>
                <Select 
                  value={formData.condition} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, condition: value }))}
                >
                  <SelectTrigger className="bg-[var(--theme-hover)] border-[var(--theme-border)] h-12" data-testid="inventory-condition-select">
                    <SelectValue placeholder="Select condition" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--theme-card)] border-[var(--theme-border)]">
                    {CONDITIONS.map((cond) => (
                      <SelectItem key={cond.id} value={cond.id} className="hover:bg-[var(--theme-hover)]">
                        <span className={cond.color}>{cond.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Visibility */}
          <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card space-y-4">
            <h2 className="font-bold text-[var(--theme-text-primary)] text-lg flex items-center gap-2">
              {formData.is_public ? <Eye className="w-5 h-5 text-[#059669]" /> : <EyeOff className="w-5 h-5 text-[var(--theme-text-secondary)]" />}
              Visibility
            </h2>
            
            <div className="flex items-center justify-between">
              <div>
                <label className="font-medium text-[var(--theme-text-primary)]">
                  {formData.is_public ? 'For Sale' : 'Unlisted'}
                </label>
                <p className="text-sm text-[var(--theme-text-secondary)] mt-1">
                  {formData.is_public 
                    ? 'Listed in the marketplace. Anyone can find and buy this item.' 
                    : 'Not visible in the marketplace, but matches with Wants.'}
                </p>
              </div>
              <Switch
                checked={formData.is_public}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_public: checked }))}
              />
            </div>

            {!formData.is_public && (
              <div className="p-3 rounded-lg bg-[#1E3A5F]/5 border border-[#1E3A5F]/10 space-y-3">
                <div className="flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-[#C9956C] mt-0.5 flex-shrink-0" />
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-[var(--theme-text-primary)]">
                      Why hide an item?
                    </p>
                    <ul className="text-sm text-[var(--theme-text-secondary)] space-y-1.5">
                      <li className="flex items-start gap-2">
                        <span className="text-[#C9956C]">•</span>
                        <span>AI detection may find items you weren't planning to sell, but someone might still be looking for them</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#C9956C]">•</span>
                        <span>You're not sure if you want to sell, but for the right price or the right buyer, you might</span>
                      </li>
                    </ul>
                    <p className="text-xs text-[var(--theme-text-muted)] italic pt-1">
                      Unlisted items only appear when they match someone's Want listing.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Location */}
          <div className="p-6 rounded-2xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card space-y-4">
            <h2 className="font-bold text-[var(--theme-text-primary)] text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#C9956C]" />
              Item Location
            </h2>
            
            {/* Location field - editable when useCustomLocation is true */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-[var(--theme-text-primary)]">
                Item location (suburb)
              </label>
              {useCustomLocation ? (
                <LocationAutocomplete
                  value={customLocation?.name || itemLocation?.name || ''}
                  onChange={(name) => setCustomLocation(prev => prev ? { ...prev, name } : { name, lat: 0, lng: 0 })}
                  onSelect={(loc) => setCustomLocation({ lat: loc.lat, lng: loc.lng, name: loc.name })}
                  placeholder="Search suburb or postcode..."
                />
              ) : (
                <div className="flex items-center gap-2 px-3 py-3 rounded-lg bg-[var(--theme-hover)] text-sm">
                  <MapPin className="w-4 h-4 text-[#059669]" />
                  <span className="text-[var(--theme-text-primary)] font-medium">{itemLocation?.name || 'Not set'}</span>
                </div>
              )}
            </div>
            
            {/* Custom location toggle */}
            <div className="flex items-center justify-between py-2">
              <div className="flex flex-col gap-1">
                <label htmlFor="custom-location-toggle" className="text-sm font-medium text-[var(--theme-text-primary)] cursor-pointer">
                  Item is at a different pickup location
                </label>
                <p className="text-xs text-[var(--theme-text-secondary)]">
                  Enable if this item is stored elsewhere (e.g., warehouse, storage unit)
                </p>
              </div>
              <Switch
                id="custom-location-toggle"
                checked={useCustomLocation}
                onCheckedChange={(checked) => {
                  setUseCustomLocation(checked);
                  if (!checked) {
                    setCustomLocation(null);
                  }
                }}
              />
            </div>
            
            <p className="text-xs text-[var(--theme-text-secondary)]">
              Your exact location is never shared. Only suburb/area is shown to buyers.
            </p>
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <Button 
              type="button"
              variant="outline"
              className="flex-1 border-[var(--theme-border)] text-[var(--theme-text-secondary)] hover:bg-[var(--theme-hover)] rounded-full h-14"
              onClick={() => setMode(null)}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="flex-[2] bg-[#1E3A5F] hover:bg-[#2D4A6F] rounded-full h-14 text-lg font-semibold"
              disabled={loading}
              data-testid="inventory-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Package className="w-5 h-5 mr-2" />
                  List Item
                </>
              )}
            </Button>
          </div>
        </form>
        </div>

        {/* Live Preview Column - Desktop only */}
        <div className="hidden lg:block lg:col-span-2">
          <div className="sticky top-24">
            <div className="flex items-center gap-2 mb-4">
              <Eye className="w-4 h-4 text-[var(--theme-text-secondary)]" />
              <span className="text-sm font-medium text-[var(--theme-text-secondary)]">Live Preview</span>
            </div>

            {/* Preview Card - Matching CatalogCard style */}
            <div className="rounded-xl border border-[var(--theme-border)] overflow-hidden bg-[var(--theme-card)] max-w-[280px]">
              {/* Preview Image */}
              <div className="relative aspect-square overflow-hidden bg-[var(--theme-hover)]">
                {formData.images.length > 0 ? (
                  <img
                    src={typeof formData.images[formData.primary_image_index] === 'object'
                      ? formData.images[formData.primary_image_index]?.url
                      : formData.images[formData.primary_image_index]}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-[var(--theme-text-muted)]">
                    <Package className="w-10 h-10 mb-2 opacity-30" />
                    <span className="text-xs opacity-50">Add photos to preview</span>
                  </div>
                )}
                {formData.images.length > 1 && (
                  <div className="absolute bottom-2 right-2 bg-black/50 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                    +{formData.images.length - 1}
                  </div>
                )}
              </div>

              {/* Preview Content */}
              <div className="p-2.5">
                <h3 className="font-semibold text-sm text-[var(--theme-text-primary)] line-clamp-2 min-h-[2.5rem] leading-tight">
                  {formData.title || 'Your item title'}
                </h3>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-base font-bold text-[#059669]">
                    {formData.estimated_value ? `$${Number(formData.estimated_value).toLocaleString()}` : '$0'}
                  </span>
                </div>
                {formData.condition && (
                  <div className="mt-1">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                      CONDITIONS.find(c => c.id === formData.condition)?.bg || 'bg-gray-100'
                    } ${
                      CONDITIONS.find(c => c.id === formData.condition)?.color || 'text-gray-600'
                    } capitalize`}>
                      {CONDITIONS.find(c => c.id === formData.condition)?.name || formData.condition}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-1 mt-1.5 text-[var(--theme-text-muted)]">
                  <MapPin className="w-3 h-3" />
                  <span className="text-[11px] truncate">
                    {customLocation?.name || 'Your location'}
                  </span>
                </div>
              </div>
            </div>

            <p className="text-[10px] text-[var(--theme-text-muted)] mt-3 text-center">
              This is how your listing will appear in the catalog
            </p>
          </div>
        </div>
        </div>
      </div>

      {/* Form Exit Dialog - triggered by React Router blocker */}
      <FormExitDialog
        isOpen={blocker.state === 'blocked'}
        onClose={() => blocker.reset?.()}
        onDiscard={() => {
          setHasUnsavedChanges(false);
          blocker.proceed?.();
        }}
      />
    </Layout>
  );
}
